'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { AnimatePresence, motion } from 'framer-motion'
import type { PanInfo } from 'framer-motion'
import styles from './HeroCarousel.module.sass'
import type { PostTileProps } from './PostTile'
import { useTranslate } from './contexts/TranslationsContextProvider'
import { ContentRenderer } from './ContentRenderer'
import { ResponsiveImage } from './ResponsiveImage'

interface HeroCarouselProps {
	items?: Array<PostTileProps>
}

const HeroCarousel = ({ items = [] }: HeroCarouselProps) => {
	const [currentSlide, setCurrentSlide] = useState(0)
	const [isMobile, setIsMobile] = useState(false)
	const [isDragging, setIsDragging] = useState(false)

	const t = useTranslate()

	useEffect(() => {
		if (items.length < 2 || isDragging) return

		const interval = setInterval(() => {
			setCurrentSlide(prev => (prev + 1) % items.length)
		}, 5000)
		return () => clearInterval(interval)
	}, [items.length, isDragging])

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setIsMobile(window.matchMedia('(max-width: 768px)').matches)
		}
	}, [])

	const handleDotClick = (index: number) => {
		setCurrentSlide(index)
	}

	const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
		if (!isMobile) return

		setIsDragging(false)
		const threshold = 50 // minimum distance required for a swipe

		if (info.offset.x < -threshold) {
			// Swiped left, go to next slide
			setCurrentSlide(prev => (prev + 1) % items.length)
		} else if (info.offset.x > threshold) {
			// Swiped right, go to previous slide
			setCurrentSlide(prev => (prev - 1 + items.length) % items.length)
		}
	}

	if (!items.length) return null

	const currentItem = items[currentSlide]
	const image = currentItem.activeRevision?.heroImage
	const type = currentItem.activeRevision?.type
	const title = currentItem.activeRevision?.localesByLocale?.title || ''
	const description = currentItem.area?.localesByLocale?.shortDescription || currentItem.activeRevision?.localesByLocale?.leadContent
	const iconLink = currentItem.area?.localesByLocale?.link?.url
	const iconImage = currentItem.area?.localesByLocale?.root?.icon?.url
	const linkUrl = currentItem.activeRevision?.post?.localesByLocale?.link?.url || '#'

	return (
		<div className={styles.heroCarouselWrapper}>
			<div className={styles.heroCarousel}>
				<div className={styles.heroCarousel__container}>
					<AnimatePresence mode="wait">
						<motion.div
							key={currentSlide}
							initial={{ opacity: 0, x: 40 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -40 }}
							transition={{ duration: 0.4 }}
							className={styles.heroCarousel__hero}
							drag={isMobile ? 'x' : false}
							dragConstraints={{ left: 0, right: 0 }}
							dragElastic={0.2}
							onDragStart={() => setIsDragging(true)}
							onDragEnd={handleDragEnd}
						>
							<ResponsiveImage
								src={image?.url || ''}
								focalPointY={image?.focalPointY}
								focalPointX={image?.focalPointX}
								alt={title}
								className={styles.heroCarousel__image}
								width={1200}
								height={1200}
								quality={98}
								priority
							/>
							<div className={styles.heroCarousel__overlay} />
							<div className={styles.heroCarousel__content}>
								<div className={styles.heroCarousel__icon}>
									{iconLink && (
										<Link href={iconLink}>
											<Image src={iconImage || ''} alt="icon" width={60} height={60} quality={98} priority />
										</Link>
									)}
								</div>

								<Link href={linkUrl} className={styles.heroCarousel__link}>
									<div>
										<h2 className={styles.heroCarousel__title}>{title}</h2>
										<div className={styles.heroCarousel__descriptionContainer}>
											{typeof description === 'string' ? (
												<p>{description}</p>
											) : description && typeof description === 'object' ? (
												// biome-ignore lint/suspicious/noExplicitAny: <explanation>
												<ContentRenderer noPaddingLastBlock content={description as any} />
											) : null}
										</div>
									</div>
									{type === 'donate' ? (
										<button type="button" className={styles.heroCarousel__button}>
											{t('donate')}
										</button>
									) : (
										<div style={{ height: '30px' }} />
									)}
								</Link>

								<div className={styles.heroCarousel__dots}>
									{items.map((item, index) => {
										const itemId = item.id || `slide-${index}`
										return (
											<div
												key={itemId}
												className={`${styles.heroCarousel__dot} ${index === currentSlide ? styles.active : ''}`}
												onClick={e => {
													e.stopPropagation()
													handleDotClick(index)
												}}
												onKeyDown={e => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.stopPropagation()
														handleDotClick(index)
													}
												}}
												role="button"
												tabIndex={0}
												aria-label={`Go to slide ${index + 1}`}
											/>
										)
									})}
								</div>
							</div>
						</motion.div>
					</AnimatePresence>
				</div>

				<div className={styles.heroCarousel__textContainer}>
					<h1>{t('hero.savePlanet.title')}</h1>
					<p className={styles.heroCarousel__subtitle}>{t('hero.savePlanet.description')}</p>
					<p className={styles.heroCarousel__subtitle}>{t('hero.savePlanet.description2')}</p>
				</div>
			</div>
		</div>
	)
}

export default HeroCarousel
